import { useCallback, useEffect, useState } from "react";

type UseElementInViewResult = {
  ref: (node: HTMLElement | null) => void;
  isInView: boolean;
};

/**
 * Becomes true once the observed element intersects the viewport (stays true afterward).
 */
export function useElementInView(options?: IntersectionObserverInit): UseElementInViewResult {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isInView, setIsInView] = useState(false);

  const ref = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (isInView || !element) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { root: null, threshold: 0, ...options }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [element, isInView, options]);

  return { ref, isInView };
}
